/**
 * CSRF Token Management
 *
 * Handles CSRF token lifecycle: fetching, caching, validation,
 * error detection, and recovery.
 */

import type { BaseQueryFn, FetchArgs, FetchBaseQueryError, QueryReturnValue, BaseQueryApi } from '@reduxjs/toolkit/query';
import { apiLogger } from '@/core/utils/logger';
import { config } from '@/config';
import { extractErrorMessage } from '@/shared/utils/error';
import {
  globalCsrfToken,
  setGlobalCsrfToken,
  csrfInitPromise,
  getCachedCsrfToken,
  clearCsrfInitPromise,
  buildCsrfCookieAttrs,
} from '@/core/providers/CsrfProvider';

// Re-export so existing `import { buildCsrfCookieAttrs } from './csrf'` callers still work
export { buildCsrfCookieAttrs };

/** Extra options passed to prepareHeaders via baseQueryWithRetry */
export interface PrepareHeadersExtra {
  isGet?: boolean;
  requestId?: string;
}

/** Type for the base query function executor (passed from main module to avoid circular deps) */
export type BaseQueryFnExecutor = BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError>;

// CSRF TOKEN LIFECYCLE

/**
 * Get CSRF token with guaranteed availability.
 * Cookie is the single source of truth. Falls back to globalCsrfToken from CsrfProvider.
 * Waits for CsrfProvider initialization if no token is available.
 */
export async function ensureCsrfToken(): Promise<string | null> {
  // 1. Check cookie first - it's the source of truth
  const cachedToken = getCachedCsrfToken();
  if (cachedToken) {
    return cachedToken;
  }

  // 2. Check globalCsrfToken (may be set during app init before cookie)
  if (globalCsrfToken) {
    return globalCsrfToken;
  }

  // 3. Wait for CsrfProvider's fetch if it's in progress
  if (csrfInitPromise) {
    apiLogger.info('[CSRF] Waiting for CsrfProvider initialization...');
    try {
      const token = await csrfInitPromise;
      if (token) {
        apiLogger.info('[CSRF] Got token from CsrfProvider');
        return token;
      }
    } catch (e) {
      apiLogger.warn('[CSRF] CsrfProvider fetch failed:', e);
    }
  }

  // 4. No token available - CsrfProvider will handle fetch/retry
  apiLogger.warn('[CSRF] No token available');
  return null;
}

/**
 * Update CSRF token from server response.
 * Updates both globalCsrfToken and cookie (single source of truth).
 */
export function updateCsrfCache(newToken: string) {
  if (newToken && newToken !== globalCsrfToken) {
    setGlobalCsrfToken(newToken);
    apiLogger.info('[CSRF] Token updated from server response');

    // Update cookie (single source of truth)
    // Uses buildCsrfCookieAttrs() which includes Domain for cross-subdomain setups
    if (typeof window !== 'undefined') {
      try {
        document.cookie = `csrf-token=${encodeURIComponent(newToken)}; ${buildCsrfCookieAttrs()}`;
      } catch (e) {
        apiLogger.warn('[CSRF] Failed to update cookie:', e);
      }
    }
  }
}

/**
 * Clear CSRF cache (for logout, token rotation, etc.)
 * Thoroughly clears all possible CSRF token storage locations
 */
export function clearCsrfCache() {
  // Clear the global variable
  setGlobalCsrfToken(null);

  // Clear the init promise so next request fetches fresh token
  clearCsrfInitPromise();

  // Clear CSRF cookies with all possible paths and names
  if (typeof window !== 'undefined') {
    try {
      // Primary cookie name with various paths
      const paths = ['/', '/api', '/auth', '/api/auth'];
      const cookieNames = ['csrf-token', 'csrf_token', 'XSRF-TOKEN'];
      const isSecure = window.location.protocol === 'https:';

      const domain = config.auth.cookieDomain;
      for (const name of cookieNames) {
        for (const path of paths) {
          document.cookie = `${name}=; Path=${path}; Max-Age=0`;
          if (isSecure) {
            document.cookie = `${name}=; Path=${path}; Max-Age=0; SameSite=None; Secure`;
          }
          document.cookie = `${name}=; Path=${path}; Max-Age=0; SameSite=Lax`;
          document.cookie = `${name}=; Path=${path}; Max-Age=0; SameSite=Strict`;
          // Also clear with Domain for cross-subdomain cookies
          if (domain) {
            document.cookie = `${name}=; Path=${path}; Domain=${domain}; Max-Age=0`;
            if (isSecure) {
              document.cookie = `${name}=; Path=${path}; Domain=${domain}; Max-Age=0; SameSite=None; Secure`;
            }
          }
        }
      }
    } catch (e) {
      apiLogger.warn('[CSRF] Error clearing cookies:', e);
    }
  }

  apiLogger.info('[CSRF] Cache cleared (variable, promise, and cookies)');
}

/**
 * Fetch a fresh CSRF token from the server.
 * This is a direct fetch bypassing RTK Query to avoid circular dependencies.
 */
export async function fetchFreshCsrfToken(): Promise<string | null> {
  try {
    const response = await fetch(`${config.api.baseUrl}/csrf-token`, {
      method: 'GET',
      credentials: 'include',
    });

    if (!response.ok) {
      apiLogger.warn('[CSRF] Failed to fetch fresh token:', response.status);
      return null;
    }

    const data = await response.json();
    const token = data.csrfToken || data.csrf_token || data.token;

    if (token) {
      updateCsrfCache(token);
      apiLogger.info('[CSRF] Fresh token fetched and cached');
      return token;
    }

    return null;
  } catch (error) {
    apiLogger.error('[CSRF] Error fetching fresh token:', error);
    return null;
  }
}

// CSRF ERROR DETECTION & RECOVERY

/**
 * Check if a 403 error is a CSRF validation failure
 * Enhanced to detect various CSRF error formats from the backend
 */
export function isCsrfError(error: FetchBaseQueryError): boolean {
  if (error.status !== 403) return false;

  const errorData = error.data as Record<string, unknown> | undefined;

  // If there's no error data at all, assume it might be CSRF (backend might return empty 403)
  if (!errorData || typeof errorData !== 'object') {
    apiLogger.warn('[isCsrfError] 403 with no error data - treating as potential CSRF error');
    return true;
  }

  // Strong signal: only CSRF middleware returns a csrfToken field
  if (typeof errorData.csrfToken === 'string') {
    apiLogger.info('[isCsrfError] Detected CSRF error via csrfToken field', { errorData });
    return true;
  }

  // Check multiple fields where CSRF error messages might appear
  // Backend sends error as an object { code, message, timestamp }, not a string
  const errorObj = errorData.error;
  const errorField = (typeof errorObj === 'string'
    ? errorObj
    : typeof errorObj === 'object' && errorObj !== null
      ? (errorObj as Record<string, unknown>).message as string || (errorObj as Record<string, unknown>).code as string || ''
      : ''
  ).toLowerCase();
  const messageField = (typeof errorData.message === 'string' ? errorData.message : '').toLowerCase();
  const statusField = (errorData.status as string | undefined)?.toLowerCase() || '';
  const combined = `${errorField} ${messageField} ${statusField}`;

  // Exclude known non-CSRF 403s before checking CSRF keywords
  const nonCsrfKeywords = ['organization', 'disabled', 'permission', 'access denied'];
  if (nonCsrfKeywords.some(kw => combined.includes(kw))) {
    apiLogger.info('[isCsrfError] 403 matches non-CSRF pattern, not treating as CSRF', { errorData });
    return false;
  }

  // CSRF-specific keywords that match backend CSRF middleware responses:
  //   error: 'CSRF token missing' + message: 'Security validation failed'
  //   error: 'CSRF token invalid' + message: 'Security validation failed'
  const csrfKeywords = [
    'csrf',
    'security validation',
    'token missing',
    'token invalid',
  ];

  const isCsrf = csrfKeywords.some(keyword => combined.includes(keyword));

  if (isCsrf) {
    apiLogger.info('[isCsrfError] Detected CSRF error', { errorData });
  }

  return isCsrf;
}

/**
 * Attempt to recover from a CSRF error by refreshing the token and retrying
 * Returns the retry result if successful, or null if recovery failed
 */
export async function attemptCsrfRecovery(
  baseQueryFn: BaseQueryFnExecutor,
  args: string | FetchArgs,
  api: BaseQueryApi,
  extraOptions: Record<string, unknown>,
  endpoint: string,
  csrfError?: FetchBaseQueryError
): Promise<QueryReturnValue<unknown, FetchBaseQueryError, object> | null> {
  apiLogger.warn('[baseQueryWithRetry] CSRF validation failed, attempting recovery', {
    endpoint
  });

  // Strategy 1: Use the fresh token from the error response (backend returns it on 403)
  const errorData = csrfError?.data as Record<string, unknown> | undefined;
  let freshToken = errorData?.csrfToken as string | undefined;

  if (freshToken) {
    apiLogger.info('[baseQueryWithRetry] Using fresh CSRF token from error response');
    clearCsrfCache();
    updateCsrfCache(freshToken);
  } else {
    // Strategy 2: Fetch a fresh token from the server
    apiLogger.info('[baseQueryWithRetry] No token in error response, fetching fresh token');
    clearCsrfCache();
    freshToken = await fetchFreshCsrfToken() || undefined;
  }

  if (!freshToken) {
    apiLogger.error('[baseQueryWithRetry] Could not obtain fresh CSRF token');
    return null;
  }

  // Retry with backoff: up to 3 attempts [500ms, 1000ms, 2000ms]
  const CSRF_RETRY_DELAYS = [500, 1000, 2000];
  for (let attempt = 0; attempt < CSRF_RETRY_DELAYS.length; attempt++) {
    try {
      apiLogger.info(`[baseQueryWithRetry] Retrying request with fresh CSRF token (attempt ${attempt + 1}/${CSRF_RETRY_DELAYS.length})`);
      const retryResult = await baseQueryFn(args, api, extraOptions);

      if (!retryResult.error) {
        apiLogger.info('[baseQueryWithRetry] CSRF retry succeeded');
        return retryResult as QueryReturnValue<unknown, FetchBaseQueryError, object>;
      }

      // If NOT a CSRF error, return immediately (different kind of failure)
      if (!isCsrfError(retryResult.error)) {
        apiLogger.warn('[baseQueryWithRetry] CSRF retry got non-CSRF error, returning', {
          endpoint, status: retryResult.error.status
        });
        return null;
      }

      // Still a CSRF error — refresh token and retry if attempts remain
      if (attempt < CSRF_RETRY_DELAYS.length - 1) {
        apiLogger.warn(`[baseQueryWithRetry] CSRF retry still failed, backing off ${CSRF_RETRY_DELAYS[attempt]}ms`);
        await new Promise(resolve => setTimeout(resolve, CSRF_RETRY_DELAYS[attempt]));
        clearCsrfCache();
        const refreshedToken = await fetchFreshCsrfToken();
        if (!refreshedToken) {
          apiLogger.error('[baseQueryWithRetry] Could not refresh CSRF token during backoff');
          return null;
        }
      } else {
        apiLogger.error('[baseQueryWithRetry] CSRF retry exhausted all attempts', {
          endpoint,
          tokenLength: freshToken.length,
          tokenPrefix: freshToken.substring(0, 8) + '...'
        });
      }
    } catch (retryError: unknown) {
      const errorDetails = {
        message: (retryError as Error)?.message || String(retryError),
        name: (retryError as Error)?.name
      };
      apiLogger.error('[baseQueryWithRetry] CSRF retry threw error:', errorDetails);
      return null;
    }
  }

  return null;
}

// CSRF HEADER ATTACHMENT (extracted from prepareHeaders)

/**
 * Attach CSRF token header to outgoing requests.
 * Handles endpoint exclusion logic, token waiting, and diagnostic logging.
 */
export async function attachCsrfHeader(
  headers: Headers,
  endpoint: string,
  extra: PrepareHeadersExtra,
): Promise<void> {
  // Get URL path from args if available (for URL-based matching)
  const urlPath = typeof extra === 'object' && extra !== null && 'url' in extra
    ? String((extra as { url?: string }).url || '')
    : '';

  // Detect excluded endpoints where CSRF is not required (GET requests, auth endpoints)
  // Match both endpoint names (from RTK Query) and URL paths for robustness
  const endpointLower = typeof endpoint === 'string' ? endpoint.toLowerCase() : '';
  const urlLower = urlPath.toLowerCase();
  const combined = `${endpointLower} ${urlLower}`;

  const isExcludedEndpoint = (
    // Auth endpoints - login (critical: must work without CSRF)
    combined.includes('login') ||
    endpointLower === 'login' ||
    urlLower.includes('/auth/login') ||
    // Other auth endpoints
    combined.includes('logout') ||
    combined.includes('validatesession') ||
    combined.includes('getsessioninfo') ||
    combined.includes('session-info') ||
    combined.includes('refreshtoken') ||
    combined.includes('refreshjwt') ||
    combined.includes('getcsrftoken') ||
    combined.includes('csrf-token') ||
    combined.includes('signupagencyowner') ||
    combined.includes('signup-agency-owner') ||
    // Signup/registration endpoints (these are public, no CSRF yet)
    combined.includes('signup') ||
    combined.includes('register') ||
    combined.includes('forgot-password') ||
    combined.includes('forgotpassword') ||
    combined.includes('reset-password') ||
    combined.includes('resetpassword') ||
    combined.includes('otp') ||
    // Read-only endpoints (GET requests by name convention)
    combined.includes('getuserprofile') ||
    combined.includes('/users/me/profile')
    // Note: 'getorcreate' endpoints (e.g., getOrCreateContactConversation) are NOT excluded
    // because they are POST mutations that can create data and need CSRF protection.
    // Note: 'onboarding/session' pattern was removed - it incorrectly excluded
    // completeOnboardingSession mutation. GET queries like getOrCreateOnboardingSession
    // are already excluded by the isGet flag at the end of this condition.
  ) || extra?.isGet;

  // Debug logging for critical endpoints to diagnose CSRF issues
  const isConversationMutation = combined.includes('getorcreate') ||
                                  combined.includes('conversation') ||
                                  combined.includes('contact-phone-number');
  if (endpointLower === 'login' || combined.includes('login') || isConversationMutation) {
    apiLogger.info(`[prepareHeaders] Critical endpoint: ${endpoint}`, {
      endpoint,
      urlPath,
      isExcludedEndpoint,
      hasCsrfCookie: !!document.cookie.match(/csrf-token=/),
      hasGlobalToken: !!globalCsrfToken
    });
  }

  if (!isExcludedEndpoint) {
    try {
      // Always wait for CSRF token on mutation endpoints - the token is required
      // and proceeding without it causes 403 errors that require retry.
      // The ensureCsrfToken() function has its own 5-second internal timeout,
      // so this won't block indefinitely.
      const csrfToken = await ensureCsrfToken();

      if (csrfToken) {
        headers.set('x-csrf-token', csrfToken);

        // Targeted diagnostics for uploads and messaging
        const isUploadEndpoint = endpoint?.includes('upload') || endpoint?.includes('profile-image') || endpoint?.includes('ProfileImage');
        if (endpoint === 'sendMessage' || isUploadEndpoint) {
          try {
            apiLogger.info(`[baseQueryWithRetry] (${endpoint}) attaching x-csrf-token`, {
              tokenLength: csrfToken.length,
              tokenPrefix: csrfToken.substring(0, 8) + '...'
            });
          } catch { }
        }
      } else {
        // Log when CSRF token is not available - helps debug
        const isUploadEndpoint = endpoint?.includes('upload') || endpoint?.includes('profile-image') || endpoint?.includes('ProfileImage');
        if (isUploadEndpoint) {
          // More severe warning for uploads - this will likely fail
          apiLogger.error(`[baseQueryWithRetry] NO CSRF TOKEN for upload endpoint ${endpoint} - request will likely fail with 403`);
        } else if (!endpoint?.includes('auth')) {
          apiLogger.warn(`[baseQueryWithRetry] No CSRF token available for ${endpoint}, proceeding without token`);
        }
      }
    } catch (error: unknown) {
      // If CSRF fetch fails, log but don't block the request
      apiLogger.warn('[baseQueryWithRetry] Failed to ensure CSRF token:', {
        message: extractErrorMessage(error, 'CSRF token fetch failed'),
      });
    }
  }
}
